// ⬡B:aba_shared.ccwa_core.components.cara_v1:CODE:shared_core_single_source:20260422⬡
// CARA — Communications And Reach Agent
//
// The canonical shared ABA chat+talk module. One source of truth for every app
// in the ecosystem: MyABA CIP, CIB, AWA Portal, Formation Portal, ccwa-standalone.
//
// Previously: CARA was copy-pasted across MyABA CIP (src/MyABA.jsx line 439) and
// AWA Portal (src/components/CARAChat.jsx). Fixes in one didn't propagate. This
// shared version lives in @aba/ccwa-core and gets imported by everyone.
//
// PROPS:
//   appScope     string   — badge label for current app context (e.g. "awa", "email")
//   userId       string   — resolved HAM identifier for streaming calls
//   apiBase      string   — ABACIA base URL (default: production abacia-services)
//   channel      string   — AIR channel (default: "cara")
//   onFullChat   fn       — optional callback to switch to main full chat
//   placement    string   — "bottom-sheet" (default) | "inline"
//
// The component renders a pulsing floating button when closed, a bottom sheet
// with message stream + input + mic when open. Streams via /api/air/stream.
//
// To add fullscreen-orb mode later, extend with placement="fullscreen" — do NOT
// create a separate TalkMode component.

import { useEffect, useRef, useState } from 'react';
import { MessageSquare, X, Send, Loader2, Mic, MicOff, Paperclip, Trash2, FileText } from 'lucide-react';
import { resolveAnewApiBase } from '@aba/api-core';

// ⬡B:ccwa_core.cara.default_api_base.reforge:CODE:legacy_to_reforge_default:20260508⬡
// ⬡B:ccwa_core.cara.default_api_base.env_first:FIX:independent_hardcode_found_20260708⬡
// CARA Ask A’NU popup defaulted to a hardcoded ababase (legacy) string,
// completely independent of packages/api-core/src/client.js's own base-URL
// resolution -- meaning the 20260708 fix that repointed CCWA's real backend
// (VITE_ABA_API_BASE=aibebase) never reached CARA at all, because CARA never
// read that env var in the first place. Found by tracing every real render
// call: ccwa-standalone's App.jsx mounts <CARA appScope="ccwa" userId={...}/>
// with no apiBase prop, so every "Ask A’NU" quick-access chat, file upload,
// and stream call was still hitting the legacy brain live, right now, silent.
// Fixed the same way client.js already resolves its base: read the real
// build-time env var first, the historical ababase default only as the net
// when that env var is genuinely absent, never a bare independent hardcode.
// Brain: rally.claude_base.envolve.intent_ccwa_four_fix_batch.20260508
const DEFAULT_API_BASE = resolveAnewApiBase();

export function CARA({
  appScope,
  userId,
  apiBase = DEFAULT_API_BASE,
  channel = 'cara',
  onFullChat,
  placement = 'bottom-sheet'
}) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // ⬡B:ccwa_core.cara.file_management:CODE:d5_cara_files:20260705⬡
  // span.task D.5. Real file upload/manage/edit against /api/files on
  // apiBase, the same real backend every other CARA call already uses.
  // No new prop for the base URL -- reuses apiBase exactly as-is.
  const [showFiles, setShowFiles] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  const loadFiles = async () => {
    if (!userId) return;
    setFilesLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/files/list?hamUid=${encodeURIComponent(userId)}`);
      const data = await res.json();
      setFiles(data.ok ? data.files : []);
    } catch (e) { setFiles([]); }
    setFilesLoading(false);
  };

  const toggleFiles = () => {
    const next = !showFiles;
    setShowFiles(next);
    if (next) loadFiles();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !userId) return;
    setUploading(true);
    try {
      const urlRes = await fetch(`${apiBase}/api/files/upload-url`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hamUid: userId, fileName: file.name })
      });
      const urlData = await urlRes.json();
      if (!urlData.ok) throw new Error(urlData.error || 'upload_url_failed');
      const putRes = await fetch(urlData.signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });
      if (!putRes.ok) throw new Error('upload_failed');
      setMessages(prev => [...prev, { role: 'aba', text: `Uploaded ${file.name}.` }]);
      if (showFiles) loadFiles();
    } catch (err) {
      setMessages(prev => [...prev, { role: 'aba', text: `Could not upload ${file.name}. Try again.` }]);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const deleteFile = async (path) => {
    if (!userId) return;
    try {
      await fetch(`${apiBase}/api/files`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hamUid: userId, path })
      });
      loadFiles();
    } catch (e) { /* list stays as-is, user can retry */ }
  };


  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      setMsg(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const ask = async () => {
    const question = msg.trim();
    if (!question || loading) return;
    const abaId = `aba-${Date.now()}`;
    setMessages(prev => [...prev, { role: 'user', text: question }, { role: 'aba', text: '', id: abaId }]);
    setMsg('');
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/air/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: question,
          user_id: userId || 'unknown',
          userId: userId || 'unknown',
          channel,
          appScope
        })
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value, { stream: true });
        for (const line of text.split('\n').filter(l => l.startsWith('data: '))) {
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === 'chunk') {
              acc += d.text;
              setMessages(prev => prev.map(m => m.id === abaId ? { ...m, text: acc } : m));
            } else if (d.type === 'done') {
              setMessages(prev => prev.map(m => m.id === abaId ? { ...m, text: d.fullResponse || acc } : m));
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages(prev => prev.map(m => m.id === abaId ? { ...m, text: 'Could not reach A’NU. Try again.' } : m));
    }
    setLoading(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="aba-cara-fab"
        onClick={() => setOpen(true)}
        aria-label="Ask A’NU"
      >
        <MessageSquare size={22} strokeWidth={2} />
      </button>
    );
  }

  return (
    <div className={`aba-cara-sheet ${placement === 'inline' ? 'inline' : 'bottom-sheet'}`}>
      <header className="aba-cara-header">
        <div className="aba-cara-header-left">
          <div className="aba-cara-badge-dot">
            <MessageSquare size={14} />
          </div>
          <span className="aba-cara-title">Ask A’NU</span>
          {appScope && <span className="aba-cara-scope">{appScope}</span>}
        </div>
        <div className="aba-cara-header-right">
          {onFullChat && (
            <button type="button" className="aba-cara-link-btn" onClick={onFullChat}>
              Full Chat
            </button>
          )}
          <button type="button" className="aba-cara-link-btn" onClick={toggleFiles}>
            Files
          </button>
          <button type="button" className="aba-cara-link-btn" onClick={() => setMessages([])}>
            Clear
          </button>
          <button
            type="button"
            className="aba-cara-icon-btn"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      {showFiles && (
        <div className="aba-cara-files-panel">
          {filesLoading && <p className="aba-cara-empty">Loading files...</p>}
          {!filesLoading && files.length === 0 && <p className="aba-cara-empty">No files yet.</p>}
          {!filesLoading && files.map((f) => (
            <div key={f.path} className="aba-cara-file-row">
              <FileText size={14} />
              <span className="aba-cara-file-name">{f.name}</span>
              <button type="button" className="aba-cara-icon-btn" onClick={() => deleteFile(f.path)} aria-label={`Delete ${f.name}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div ref={scrollRef} className="aba-cara-stream">
        {messages.length === 0 && (
          <p className="aba-cara-empty">Ask A’NU anything{appScope ? ` about ${appScope}` : ''}.</p>
        )}
        {messages.map((m, i) => (
          <div key={m.id || i} className={`aba-cara-row ${m.role === 'user' ? 'user' : 'aba'}`}>
            <div className="aba-cara-bubble">
              <p>{m.text || '...'}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="aba-cara-inputbar">
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
        <button
          type="button"
          className="aba-cara-icon-btn"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={uploading}
          aria-label="Attach a file"
        >
          {uploading ? <Loader2 size={16} className="aba-spin" /> : <Paperclip size={16} />}
        </button>
        <button
          type="button"
          className={`aba-cara-mic ${isListening ? 'listening' : ''}`}
          onClick={toggleListening}
          aria-label={isListening ? 'Stop listening' : 'Start voice input'}
        >
          {isListening ? <Mic size={16} /> : <MicOff size={16} />}
        </button>
        <input
          ref={inputRef}
          type="text"
          className="aba-cara-input"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={isListening ? 'Listening...' : 'Ask A’NU anything...'}
          disabled={loading}
        />
        <button
          type="button"
          className="aba-cara-send"
          onClick={ask}
          disabled={loading || !msg.trim()}
          aria-label="Send"
        >
          {loading ? <Loader2 size={16} className="aba-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
